import React, { useState, useCallback, useEffect } from "react";
import { Form } from "react-bootstrap";
import {
  Plus, Pencil, Trash2, ClipboardList, ChevronRight, Link2, Unlink, MoveRight,
} from "lucide-react";
import * as plmSvc from "../../services/plmService";
import EntityFormDialog from "./EntityFormDialog";
import TagInput from "./TagInput";

const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const STATUSES = ["Draft", "Approved", "In Progress", "Done", "Rejected"];

const EMPTY = {
  title: "", description: "", requirementTypeId: "", priority: "Medium",
  status: "Draft", parentRequirementId: "", tags: [],
};

const priorityColor = (p) => {
  switch (p) { case "Critical": return "red"; case "High": return "yellow"; case "Medium": return "blue"; default: return "gray"; }
};

const statusColor = (s) => {
  switch (s) { case "Done": return "green"; case "Approved": return "blue"; case "Rejected": return "red"; case "In Progress": return "purple"; default: return "gray"; }
};

const ChildRequirement = ({ req, depth, token, onEdit, onDelete, onMove, onCreate, onCoverage, onRemoveCoverage }) => {
  const [expanded, setExpanded] = useState(false);
  const [childDetail, setChildDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (!expanded && !childDetail) {
      setLoading(true);
      try {
        const data = await plmSvc.fetchRequirementByIdApi({ token, requirementId: req.id });
        setChildDetail(data);
      } catch {}
      finally { setLoading(false); }
    }
    setExpanded(!expanded);
  };

  const detail = childDetail || req;

  return (
    <div className="plm-req-child">
      <div className="d-flex align-items-center justify-content-between py-1" role="button" onClick={toggle}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <ChevronRight size={14} className={`plm-chevron ${expanded ? "open" : ""}`} />
          <span style={{ fontWeight: 600, fontSize: "0.85rem" }}>{req.title}</span>
          <span className={`plm-badge ${priorityColor(req.priority)}`}>{req.priority}</span>
          <span className={`plm-badge ${statusColor(req.status)}`}>{req.status}</span>
        </div>
        <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
          <button className="plm-btn-ghost" style={{ padding: "2px 5px" }} title="Move" onClick={() => onMove(req)}><MoveRight size={12} /></button>
          <button className="plm-btn-ghost" style={{ padding: "2px 5px" }} onClick={() => onEdit(req)}><Pencil size={12} /></button>
          <button className="plm-btn-ghost danger" style={{ padding: "2px 5px" }} onClick={() => onDelete(req.id)}><Trash2 size={12} /></button>
        </div>
      </div>
      {expanded && (
        <div style={{ fontSize: "0.82rem", paddingTop: 4, paddingBottom: 8 }}>
          {loading ? <div style={{ color: "var(--plm-text-muted)" }}>Loading...</div> : (
            <>
              {detail.description && <p style={{ color: "var(--plm-text-muted)", marginBottom: 8 }}>{detail.description}</p>}

              {detail.children?.length > 0 && (
                <div className="mb-2">
                  <div style={{ fontWeight: 600, fontSize: "0.8rem", marginBottom: 4 }}>Sub-Requirements:</div>
                  {detail.children.map((c) => (
                    <ChildRequirement
                      key={c.id} req={c} depth={depth + 1} token={token}
                      onEdit={onEdit} onDelete={onDelete} onMove={onMove}
                      onCreate={onCreate} onCoverage={onCoverage}
                      onRemoveCoverage={onRemoveCoverage}
                    />
                  ))}
                </div>
              )}
              <button className="plm-btn-ghost mb-2" style={{ fontSize: "0.78rem" }} onClick={() => onCreate(req.id)}>
                <Plus size={12} /> Add Sub-Requirement
              </button>

              <div style={{ fontWeight: 600, fontSize: "0.8rem", marginBottom: 4 }}>Journey Step Coverage:</div>
              {detail.coverageSteps?.length > 0 ? (
                <div className="d-flex flex-column gap-1 mb-2">
                  {detail.coverageSteps.map((c) => (
                    <div key={c.journey_step_id} className="d-flex justify-content-between align-items-center"
                      style={{ padding: "4px 8px", background: "#f8fafc", borderRadius: 6, fontSize: "0.8rem" }}>
                      <span>{c.journey_map_title} -- Step #{c.step_order} ({c.phase_name})</span>
                      <button className="plm-btn-ghost danger" style={{ padding: "2px 4px" }}
                        onClick={() => onRemoveCoverage(req.id, c.journey_step_id)}>
                        <Unlink size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "var(--plm-text-muted)", fontSize: "0.78rem", marginBottom: 8 }}>No coverage links yet.</div>
              )}
              <button className="plm-btn-ghost" style={{ fontSize: "0.78rem" }} onClick={() => onCoverage(req.id)}>
                <Link2 size={12} /> Link Journey Step
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const RequirementsTab = ({ token, plm }) => {
  const {
    requirements, requirementTypes, tags: allTags, journeyMaps,
    createRequirement, updateRequirement, deleteRequirement,
    fetchRequirements, fetchRequirementTypes,
  } = plm;

  const tagNames = allTags.map((t) => t.name);

  const [showDlg, setShowDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [showCoverageDlg, setShowCoverageDlg] = useState(false);
  const [coverageReqId, setCoverageReqId] = useState(null);
  const [journeySteps, setJourneySteps] = useState([]);
  const [selectedStepId, setSelectedStepId] = useState("");

  const [showMoveDlg, setShowMoveDlg] = useState(false);
  const [moveReqId, setMoveReqId] = useState(null);
  const [moveReqTitle, setMoveReqTitle] = useState("");
  const [allReqsFlat, setAllReqsFlat] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState("");

  const loadDetail = useCallback(async (id) => {
    setLoadingDetail(true);
    try {
      const data = await plmSvc.fetchRequirementByIdApi({ token, requirementId: id });
      setDetail(data);
    } catch { setDetail(null); }
    finally { setLoadingDetail(false); }
  }, [token]);

  useEffect(() => {
    if (expandedId) loadDetail(expandedId);
  }, [expandedId, loadDetail]);

  const openCreate = (parentId) => {
    setEditing(null);
    setForm({ ...EMPTY, parentRequirementId: parentId || "" });
    setShowDlg(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      title: r.title, description: r.description || "",
      requirementTypeId: r.requirementTypeId || "",
      priority: r.priority || "Medium", status: r.status || "Draft",
      parentRequirementId: r.parentRequirementId || "",
      tags: r.tags || [],
    });
    setShowDlg(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...form };
    if (!body.requirementTypeId) delete body.requirementTypeId;
    if (!body.parentRequirementId) delete body.parentRequirementId;
    if (editing) {
      await updateRequirement(editing.id, body);
    } else {
      await createRequirement(body);
    }
    setSaving(false);
    setShowDlg(false);
    if (expandedId) loadDetail(expandedId);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this requirement?")) return;
    await deleteRequirement(id);
    if (expandedId === id) { setExpandedId(null); setDetail(null); }
    else if (expandedId) await loadDetail(expandedId);
  };

  const openCoverage = async (reqId) => {
    setCoverageReqId(reqId);
    setSelectedStepId("");
    const allSteps = [];
    for (const jm of journeyMaps) {
      try {
        const d = await plmSvc.fetchJourneyMapByIdApi({ token, journeyMapId: jm.id });
        (d.steps || []).forEach((s) => allSteps.push({ ...s, journeyTitle: jm.title }));
      } catch {}
    }
    setJourneySteps(allSteps);
    setShowCoverageDlg(true);
  };

  const handleAddCoverage = async (e) => {
    e.preventDefault();
    if (!selectedStepId) return;
    setSaving(true);
    await plmSvc.addCoverageApi({ token, requirementId: coverageReqId, journeyStepId: parseInt(selectedStepId) });
    if (expandedId) await loadDetail(expandedId);
    setSaving(false);
    setShowCoverageDlg(false);
  };

  const handleRemoveCoverage = async (reqId, stepId) => {
    await plmSvc.removeCoverageApi({ token, requirementId: reqId, journeyStepId: stepId });
    if (expandedId) await loadDetail(expandedId);
  };

  const openMove = async (req) => {
    setMoveReqId(req.id);
    setMoveReqTitle(req.title);
    setSelectedParentId(req.parentRequirementId ? String(req.parentRequirementId) : "");
    try {
      const flat = await plmSvc.fetchRequirementsFlatApi({ token, productId: undefined });
      const exclude = new Set([req.id]);
      const addDescendants = (parentId) => {
        flat.filter(r => r.parentRequirementId === parentId).forEach(r => {
          exclude.add(r.id);
          addDescendants(r.id);
        });
      };
      addDescendants(req.id);
      setAllReqsFlat(flat.filter(r => !exclude.has(r.id)));
    } catch {
      setAllReqsFlat([]);
    }
    setShowMoveDlg(true);
  };

  const handleMove = async (e) => {
    e.preventDefault();
    setSaving(true);
    const newParent = selectedParentId ? parseInt(selectedParentId) : null;
    await updateRequirement(moveReqId, { parentRequirementId: newParent });
    await fetchRequirements();
    if (expandedId) loadDetail(expandedId);
    setSaving(false);
    setShowMoveDlg(false);
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  // Stats
  const totalReqs = requirements.length;
  const doneReqs = requirements.filter(r => r.status === "Done").length;
  const criticalReqs = requirements.filter(r => r.priority === "Critical").length;

  return (
    <div>
      <div className="plm-section-header">
        <h2 className="plm-section-title">
          <span className="icon-wrap"><ClipboardList size={20} /></span>
          Requirements
        </h2>
        <button className="plm-btn-primary" onClick={() => openCreate()}>
          <Plus size={16} /> New Requirement
        </button>
      </div>

      {/* Quick stats */}
      {totalReqs > 0 && (
        <div className="plm-stats">
          <div className="plm-stat-card">
            <div className="plm-stat-value">{totalReqs}</div>
            <div className="plm-stat-label">Total</div>
          </div>
          <div className="plm-stat-card">
            <div className="plm-stat-value" style={{ color: "var(--plm-success)" }}>{doneReqs}</div>
            <div className="plm-stat-label">Done</div>
          </div>
          <div className="plm-stat-card">
            <div className="plm-stat-value" style={{ color: "var(--plm-danger)" }}>{criticalReqs}</div>
            <div className="plm-stat-label">Critical</div>
          </div>
        </div>
      )}

      {requirements.length === 0 ? (
        <div className="plm-empty">
          <div className="plm-empty-icon"><ClipboardList size={28} /></div>
          <div className="plm-empty-title">No requirements yet</div>
          <div className="plm-empty-desc">Define what needs to be built.</div>
          <button className="plm-btn-primary" onClick={() => openCreate()}>
            <Plus size={16} /> Create Requirement
          </button>
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {requirements.map((r) => (
            <div key={r.id} className="plm-card">
              <div
                className="plm-card-header"
                onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
              >
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <ChevronRight size={16} className={`plm-chevron ${expandedId === r.id ? "open" : ""}`} />
                  <span style={{ fontWeight: 700 }}>{r.title}</span>
                  <span className={`plm-badge ${priorityColor(r.priority)}`}>{r.priority}</span>
                  <span className={`plm-badge ${statusColor(r.status)}`}>{r.status}</span>
                  {r.requirementTypeName && <span className="plm-badge dark">{r.requirementTypeName}</span>}
                </div>
                <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button className="plm-btn-ghost" title="Move" onClick={() => openMove(r)}><MoveRight size={14} /></button>
                  <button className="plm-btn-ghost" onClick={() => openEdit(r)}><Pencil size={14} /></button>
                  <button className="plm-btn-ghost danger" onClick={() => handleDelete(r.id)}><Trash2 size={14} /></button>
                </div>
              </div>

              {expandedId === r.id && (
                <div className="plm-card-body" style={{ paddingTop: 14, fontSize: "0.85rem" }}>
                  {loadingDetail ? (
                    <div className="plm-loading"><div className="plm-loading-dots"><span /><span /><span /></div></div>
                  ) : detail ? (
                    <>
                      {detail.description && <p style={{ color: "var(--plm-text-muted)", marginBottom: 12 }}>{detail.description}</p>}
                      {detail.tags?.length > 0 && (
                        <div className="d-flex flex-wrap gap-1 mb-3">
                          {detail.tags.map((t) => <span key={t} className="plm-tag">{t}</span>)}
                        </div>
                      )}

                      {detail.children?.length > 0 && (
                        <div className="mb-3">
                          <div style={{ fontWeight: 600, marginBottom: 6 }}>Sub-Requirements:</div>
                          {detail.children.map((c) => (
                            <ChildRequirement
                              key={c.id} req={c} depth={1} token={token}
                              onEdit={openEdit} onDelete={handleDelete} onMove={openMove}
                              onCreate={openCreate} onCoverage={openCoverage}
                              onRemoveCoverage={handleRemoveCoverage}
                            />
                          ))}
                        </div>
                      )}
                      <button className="plm-btn-ghost mb-3" onClick={() => openCreate(r.id)}>
                        <Plus size={14} /> Add Sub-Requirement
                      </button>

                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Journey Step Coverage:</div>
                      {detail.coverageSteps?.length > 0 ? (
                        <div className="d-flex flex-column gap-1 mb-3">
                          {detail.coverageSteps.map((c) => (
                            <div key={c.journey_step_id} className="d-flex justify-content-between align-items-center"
                              style={{ padding: "6px 12px", background: "#f8fafc", borderRadius: 8 }}>
                              <span>{c.journey_map_title} -- Step #{c.step_order} ({c.phase_name})</span>
                              <button className="plm-btn-ghost danger" style={{ padding: "3px 6px" }}
                                onClick={() => handleRemoveCoverage(r.id, c.journey_step_id)}>
                                <Unlink size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: "var(--plm-text-muted)", fontSize: "0.82rem", marginBottom: 12 }}>No coverage links yet.</div>
                      )}
                      <button className="plm-btn-ghost" onClick={() => openCoverage(r.id)}>
                        <Link2 size={14} /> Link Journey Step
                      </button>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Requirement Form */}
      <EntityFormDialog show={showDlg} onClose={() => setShowDlg(false)}
        title={editing ? "Edit Requirement" : "New Requirement"} onSubmit={handleSubmit} saving={saving}>
        <Form.Group className="mb-3">
          <Form.Label>Title *</Form.Label>
          <Form.Control required value={form.title} onChange={(e) => set("title", e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control as="textarea" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} />
        </Form.Group>
        <div className="row">
          <Form.Group className="col-6 mb-3">
            <Form.Label>Priority</Form.Label>
            <Form.Select value={form.priority} onChange={(e) => set("priority", e.target.value)}>
              {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="col-6 mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select value={form.status} onChange={(e) => set("status", e.target.value)}>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </Form.Select>
          </Form.Group>
        </div>
        <Form.Group className="mb-3">
          <Form.Label>Type</Form.Label>
          <Form.Select value={form.requirementTypeId} onChange={(e) => set("requirementTypeId", e.target.value)}>
            <option value="">-- None --</option>
            {requirementTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Tags</Form.Label>
          <TagInput tags={form.tags} onChange={(t) => set("tags", t)} suggestions={tagNames} />
        </Form.Group>
      </EntityFormDialog>

      {/* Coverage Link Dialog */}
      <EntityFormDialog show={showCoverageDlg} onClose={() => setShowCoverageDlg(false)}
        title="Link Journey Step" onSubmit={handleAddCoverage} saving={saving} submitLabel="Link">
        <Form.Group>
          <Form.Label>Select Journey Step</Form.Label>
          <Form.Select required value={selectedStepId} onChange={(e) => setSelectedStepId(e.target.value)}>
            <option value="">-- Select --</option>
            {journeySteps.map((s) => (
              <option key={s.id} value={s.id}>
                {s.journeyTitle} -- #{s.stepOrder} {s.phaseName}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </EntityFormDialog>

      {/* Move Dialog */}
      <EntityFormDialog show={showMoveDlg} onClose={() => setShowMoveDlg(false)}
        title={`Move: ${moveReqTitle}`} onSubmit={handleMove} saving={saving} submitLabel="Move">
        <Form.Group>
          <Form.Label>Parent Requirement</Form.Label>
          <Form.Select value={selectedParentId} onChange={(e) => setSelectedParentId(e.target.value)}>
            <option value="">-- None (top-level) --</option>
            {allReqsFlat.map((r) => (
              <option key={r.id} value={r.id}>{r.title}</option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Select a parent to make this a sub-requirement, or choose "None" to make it top-level.
          </Form.Text>
        </Form.Group>
      </EntityFormDialog>
    </div>
  );
};

export default RequirementsTab;
